'use client';

/**
 * Chat panel for one session (/sessions page) — classic-chat parity:
 * image attach (button / paste / drag-drop), message queueing while
 * working, retry on error, clear chat, and the full status bar
 * (model · thinking · ctx% · $ · time · ♥ · ¶ │ 5h/7d) above the input.
 * `main` targets the gateway's main session (the pinned General).
 */
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { MarkdownBlock } from './Markdown';
import { ToolBlock } from './ToolBlock';
import { useSessionChat } from './useSessionChat';
import { SessionStatusBar } from './SessionStatusBar';
import type { ChatMessage, ChatImage } from './types';

import { memo } from 'react';

// Message timestamp — "6:42 PM" for today, "Jun 14, 6:42 PM" if older.
function fmtTs(ts?: number): string {
  if (!ts) return '';
  const d = new Date(ts);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const today = new Date().toDateString() === d.toDateString();
  return today ? time : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
}

// Searchable plain text for a message (content + any text parts + tool names).
function textOf(m: ChatMessage): string {
  if (m.parts && m.parts.length) {
    return m.parts.map((p) => p.type === 'text' ? p.content : p.type === 'thinking' ? '' : `${p.tool?.name || ''} ${typeof p.tool?.input === 'string' ? p.tool.input : JSON.stringify(p.tool?.input || '')}`).join(' ');
  }
  return String(m.content || '');
}

// Hover copy button — copies message text to the clipboard, checks for 1.2s.
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }, [text]);
  return (
    <button data-nodrag onClick={copy} title={copied ? 'Copied' : 'Copy text'}
      className="msg-copy-btn"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', marginTop: 4,
        borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 10, lineHeight: 1,
        background: 'rgba(255,255,255,0.06)', color: copied ? '#4ade80' : '#8b95a5' }}>
      {copied ? (
        <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5"/></svg>Copied</>
      ) : (
        <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy</>
      )}
    </button>
  );
}

function DeleteButton({ onDelete }: { onDelete: () => void }) {
  return (
    <button data-nodrag onClick={(e) => { e.stopPropagation(); onDelete(); }}
      title="Delete message — forgets it from memory"
      className="msg-copy-btn msg-del-btn"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', marginTop: 4,
        borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 10, lineHeight: 1,
        background: 'rgba(255,255,255,0.06)', color: '#8b95a5' }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
    </button>
  );
}

const MessageRow = memo(function MessageRow({ m, match, current, onDelete }: { m: ChatMessage; match?: boolean; current?: boolean; onDelete?: (id: string) => void }) {
  const isUser = m.role === 'user';
  const ring = current ? ' ring-2 ring-yellow-400 rounded-2xl' : match ? ' ring-1 ring-yellow-400/40 rounded-2xl' : '';
  return (
    <div id={`msg-${m.id}`} className={(isUser ? 'msg-row flex justify-end min-w-0' : 'msg-row flex justify-start min-w-0')}>
      <div className={(isUser ? 'flex flex-col items-end max-w-[80%] min-w-0' : 'flex flex-col items-start max-w-[92%] min-w-0') + ring}>
        <div className={isUser
          ? 'max-w-full break-words rounded-2xl rounded-br-sm bg-blue-600/25 border border-blue-500/20 px-4 py-2.5 text-sm text-gray-100 whitespace-pre-wrap'
          : 'w-full min-w-0 text-sm text-gray-200'}>
          {isUser ? m.content : <MessageBody m={m} />}
        </div>
        <div className="flex items-center gap-1">
          <CopyButton text={textOf(m)} />
          {onDelete && <DeleteButton onDelete={() => onDelete(m.id)} />}
        </div>
        {m.timestamp ? (
          <span className="text-[10px] text-gray-500 mt-1 px-1 select-none" title={new Date(m.timestamp).toLocaleString()}>
            {fmtTs(m.timestamp)}
          </span>
        ) : null}
      </div>
    </div>
  );
});

function MessageBody({ m }: { m: ChatMessage }) {
  if (m.parts && m.parts.length > 0) {
    return (
      <>
        {m.parts.map((p, i) =>
          p.type === 'text'
            ? <MarkdownBlock key={i} text={p.content} />
            : p.type === 'thinking'
              ? <div key={i} className="text-xs text-gray-500 italic whitespace-pre-wrap border-l-2 border-gray-700 pl-2 my-1">{p.content}</div>
              : <ToolBlock key={p.tool.id || i} name={p.tool.name} input={p.tool.input} output={p.tool.output} status={p.tool.status} />,
        )}
      </>
    );
  }
  return <MarkdownBlock text={m.content} />;
}

// Anthropic caps every image at 2000px on its longest edge once a request
// carries many images (>20, history included) — a Retina screenshot (~2880px)
// then 400s the WHOLE request. So we downscale to ≤2000px on attach. We also
// keep at most MAX_IMAGES per message to stay under that many-image threshold.
const MAX_EDGE = 2000;
const MAX_IMAGES = 20;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = url;
  });
}

function fileToBase64(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1] || null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

async function fileToImage(file: File): Promise<ChatImage | null> {
  if (!file.type.startsWith('image/')) return null;
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const longest = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = Math.min(1, MAX_EDGE / longest);
    // already within bounds and a modest payload → keep original bytes (no
    // re-encode, preserves quality and the source format / transparency)
    if (scale === 1 && file.size <= 1_500_000) {
      const data = await fileToBase64(file);
      return data ? { data, media_type: file.type } : null;
    }
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      const data = await fileToBase64(file);
      return data ? { data, media_type: file.type } : null;
    }
    ctx.drawImage(img, 0, 0, w, h);
    // JPEG keeps big screenshots small; transparency isn't needed for these
    const data = canvas.toDataURL('image/jpeg', 0.9).split(',')[1];
    return data ? { data, media_type: 'image/jpeg' } : null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function SessionChat({ slug, name, main, onRename }: { slug: string; name: string; main?: boolean; onRename?: () => void }) {
  const { messages, streaming, queued, streamText, streamParts, thinking, error, canRetry, historyHydrated, send, abort, retry, clear, deleteMessage, restoreMessage } = useSessionChat(slug, main);
  // Undo buffer for a just-deleted message (recoverable ~4.5s, then permanent).
  const [pendingDelete, setPendingDelete] = useState<{ msg: ChatMessage; idx: number } | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleDeleteMessage = useCallback(async (id: string) => {
    const removed = await deleteMessage(id);
    if (!removed) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setPendingDelete(removed);
    undoTimerRef.current = setTimeout(() => setPendingDelete(null), 4500);
  }, [deleteMessage]);
  const handleUndoDelete = useCallback(() => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setPendingDelete((prev) => { if (prev) restoreMessage(prev.msg, prev.idx); return null; });
  }, [restoreMessage]);
  const [pendingImages, setPendingImages] = useState<ChatImage[]>([]);
  const [pendingDocs, setPendingDocs] = useState<Array<{ name: string; path: string }>>([]);
  const [attachNote, setAttachNote] = useState<string | null>(null);
  // mirror pendingImages in a ref so addFiles can read the current count
  // without a stale closure (addFiles is a []-dep useCallback)
  const pendingRef = useRef<ChatImage[]>([]);
  useEffect(() => { pendingRef.current = pendingImages; }, [pendingImages]);
  const [dragOver, setDragOver] = useState(false);
  const [windowSize, setWindowSize] = useState(80);
  const visibleMessages = messages.length > windowSize ? messages.slice(messages.length - windowSize) : messages;
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Follow new output only when you're already at the bottom. If you've scrolled
  // up to read, leave you there — don't yank you down on streaming/new messages.
  // (ref, not state, so scrolling doesn't re-render and there's no stale closure.)
  const atBottomRef = useRef(true);
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  // ── In-chat search: type a word, jump straight to each match ──────────────
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Claire's dress-up game (Boss 2026-07-15): a big launch button opens the game in its OWN
  // window (window.open) so Boss can keep chatting/editing while she plays.
  const isClaire = slug === 'claire';
  const [gamesOpen, setGamesOpen] = useState(false);
  const CLAIRE_GAMES = [
    { url: '/claire-dressup/index.html',  name: 'Dress-Up',        emoji: '🎀', bg: 'linear-gradient(135deg,#ff7ac0,#b57be8)' },
    { url: '/claire-fly/index.html',      name: 'Fly With Sprout', emoji: '🐰', bg: 'linear-gradient(135deg,#5fc8ff,#7be8b5)' },
    { url: '/claire-aquarium/index.html', name: 'Aquarium',        emoji: '🐠', bg: 'linear-gradient(135deg,#2bb0e6,#1670b0)' },
    { url: '/claire-paint/index.html',    name: 'Painting Studio', emoji: '🎨', bg: 'linear-gradient(135deg,#ff8ac0,#ff5fa8)' },
    { url: '/claire-cake/index.html',     name: 'Cake Craft',      emoji: '🎂', bg: 'linear-gradient(135deg,#ff9ec9,#b57be8)' },
    { url: '/claire-house/index.html',    name: 'House Builder',   emoji: '🏠', bg: 'linear-gradient(135deg,#ffb14d,#e8443a)' },
  ];
  const [matchIdx, setMatchIdx] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Search ALL messages (not just the visible window) so older matches are found.
  const matchIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as string[];
    return messages.filter((m) => textOf(m).toLowerCase().includes(q)).map((m) => m.id);
  }, [query, messages]);
  const matchSet = useMemo(() => new Set(matchIds), [matchIds]);
  const currentId = matchIds[matchIdx];

  // Jump to match `idx`: expand the window if the match is scrolled-out of it,
  // then smooth-scroll it to center. Wraps around.
  const jumpTo = useCallback((idx: number) => {
    if (!matchIds.length) return;
    const i = ((idx % matchIds.length) + matchIds.length) % matchIds.length;
    setMatchIdx(i);
    const id = matchIds[i];
    const mi = messages.findIndex((m) => m.id === id);
    if (mi >= 0 && mi < messages.length - windowSize) setWindowSize(messages.length - mi + 5);
    atBottomRef.current = false; // searching shouldn't yank you to the bottom
    requestAnimationFrame(() => {
      requestAnimationFrame(() => document.getElementById(`msg-${id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    });
  }, [matchIds, messages, windowSize]);

  // New query → jump to the first match automatically.
  useEffect(() => { setMatchIdx(0); if (matchIds.length) jumpTo(0); /* eslint-disable-next-line */ }, [query, matchIds.length]);

  // ⌘F / Ctrl-F opens search (and re-focuses it); Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        setSearchOpen(true);
        requestAnimationFrame(() => searchInputRef.current?.focus());
      } else if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false); setQuery('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen]);

  // Highlight every visible occurrence of the query IN THE TEXT via the CSS
  // Custom Highlight API — it paints text ranges without mutating the DOM, so it
  // works over markdown, code blocks, everything (no re-parsing, no broken
  // syntax). The current match's occurrences get a brighter highlight. Rebuilt
  // after render whenever the query, matches, window, or content changes.
  useEffect(() => {
    const H = (typeof CSS !== 'undefined' && (CSS as any).highlights) ? (CSS as any).highlights : null;
    const HighlightCtor = (window as any).Highlight;
    if (!H || !HighlightCtor) return; // unsupported browser → ring-only fallback
    H.delete('chat-search'); H.delete('chat-search-current');
    const q = query.trim().toLowerCase();
    const root = scrollRef.current;
    if (!q || !root) return;
    const id = requestAnimationFrame(() => {
      const all: Range[] = [];
      const cur: Range[] = [];
      const MAX = 4000; // cap: a 1-char query can match tens of thousands of spots
      const currentEl = currentId ? document.getElementById(`msg-${currentId}`) : null;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node: Node | null;
      while ((node = walker.nextNode()) && all.length < MAX) {
        const text = node.nodeValue || '';
        const lower = text.toLowerCase();
        let i = lower.indexOf(q);
        while (i !== -1 && all.length < MAX) {
          try {
            const r = document.createRange();
            r.setStart(node, i); r.setEnd(node, i + q.length);
            all.push(r);
            if (currentEl && currentEl.contains(node)) cur.push(r);
          } catch { /* skip bad range */ }
          i = lower.indexOf(q, i + q.length);
        }
      }
      // Build via .add() (Highlight is Set-like) — NOT `new HighlightCtor(...all)`:
      // spreading thousands of ranges as call args overflows the stack on a
      // short query that matches everywhere ("Maximum call stack size exceeded").
      if (all.length) { const h = new HighlightCtor(); for (const r of all) h.add(r); H.set('chat-search', h); }
      if (cur.length) { const h = new HighlightCtor(); for (const r of cur) h.add(r); H.set('chat-search-current', h); }
    });
    return () => cancelAnimationFrame(id);
  }, [query, matchIds, currentId, windowSize, messages.length, streamText]);

  // Clear highlights when this pane unmounts.
  useEffect(() => () => {
    const H = (typeof CSS !== 'undefined' && (CSS as any).highlights) ? (CSS as any).highlights : null;
    if (H) { H.delete('chat-search'); H.delete('chat-search-current'); }
  }, []);

  // Inject the ::highlight() rules at RUNTIME (once). They can't live in
  // globals.css — Turbopack's build-time CSS parser rejects ::highlight() as an
  // unknown pseudo-element — but Chromium parses them fine at runtime.
  useEffect(() => {
    if (document.getElementById('chat-search-hl-style')) return;
    const el = document.createElement('style');
    el.id = 'chat-search-hl-style';
    el.textContent =
      '::highlight(chat-search){background-color:rgba(250,204,21,0.30);color:inherit;}' +
      '::highlight(chat-search-current){background-color:rgba(250,204,21,0.90);color:#1a1a1a;}';
    document.head.appendChild(el);
  }, []);

  useEffect(() => {
    // Instant snap (not smooth): keeps the view pinned to the bottom edge as
    // output streams in. Smooth scrolling lags behind fast/large output and can
    // accidentally read "not at bottom" mid-animation, disengaging the follow.
    if (atBottomRef.current) bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, streamText, streamParts.length]);

  /* VOICE ROUTING (Boss 2026-07-11 'send my transcription into whatever session is open'):
     when THIS session is the one on screen, it — not the hidden main chat — takes the orb's
     turn. __general is published as '__general' but mounts with slug 'general', so match both. */
  const voiceTurn = useRef(false);
  const spokenLen = useRef(0);
  useEffect(() => {
    const onVoiceSend = (e: Event) => {
      const { text, context } = (e as CustomEvent).detail || {};
      if (!text) return;
      const target = (window as any).__beastVoiceTarget;
      const mine = target === slug || (main && target === '__general');
      if (!mine) return; // not the on-screen session
      // SINGLE-FLIGHT (Boss 2026-07-19 double/triple-send fix): only the first surface consumes a turn.
      const g = (window as any); const vid = (e as CustomEvent).detail?.vid || `t:${text}`;
      g.__beastVoiceSeen = g.__beastVoiceSeen || new Map();
      const nowT = Date.now(); const seenT = g.__beastVoiceSeen.get(vid);
      if (seenT && nowT - seenT < 5000) return; g.__beastVoiceSeen.set(vid, nowT);
      window.dispatchEvent(new CustomEvent('beast:voice-send-ack'));
      voiceTurn.current = true; spokenLen.current = 0;
      send(`🎙 ${text}`, undefined, context);
    };
    window.addEventListener('beast:voice-send', onVoiceSend);
    return () => window.removeEventListener('beast:voice-send', onVoiceSend);
  }, [send, slug, main]);

  /* SINGLE SPEAK PATH (Boss 2026-07-15 rebuild 'one identify step, one speak path, one clean
     loop'): a voice turn streams its reply back to the orb via beast:voice-delta, and the orb
     alone speaks it (phase GREEN) and re-arms the mic (phase ORANGE) when done. No competing
     direct-speak path — that fought the orb's phase and produced orange-instead-of-green with no
     audio. This is the same path Boss's main chat uses; Claire's card is identical. */
  useEffect(() => {
    if (!voiceTurn.current) return;
    if (streamText.length > spokenLen.current) {
      const tail = streamText.slice(spokenLen.current);
      spokenLen.current = streamText.length;
      window.dispatchEvent(new CustomEvent('beast:voice-delta', { detail: { text: tail } }));
    }
  }, [streamText]);
  useEffect(() => {
    if (!streaming && voiceTurn.current) {
      voiceTurn.current = false; spokenLen.current = 0;
      window.dispatchEvent(new CustomEvent('beast:voice-done'));
    }
  }, [streaming]);

  const handleSend = useCallback((text: string) => {
    atBottomRef.current = true; // sending your own message always snaps to bottom
    const imgs = pendingImages;
    const docs = pendingDocs;
    setPendingImages([]);
    setPendingDocs([]);
    setAttachNote(null);
    // Tell the agent where its documents landed so it reads them from disk.
    const docNote = docs.length
      ? `\n\n[Documents uploaded to this project — read them with your file tools]\n${docs.map((d) => `• ${d.path}`).join('\n')}`
      : '';
    send((text + docNote).trim(), imgs.length ? imgs : undefined);
  }, [pendingImages, pendingDocs, send]);

  // Split files: images go inline (vision); everything else is a DOCUMENT —
  // uploaded into THIS session's project folder (<project>/uploads/) and the
  // agent reads it from disk.
  const addFiles = useCallback(async (files: FileList | File[]) => {
    const list = [...files];
    const images = list.filter((f) => f.type.startsWith('image/'));
    const docs = list.filter((f) => !f.type.startsWith('image/'));

    if (images.length) {
      const imgs = (await Promise.all(images.map(fileToImage))).filter(Boolean) as ChatImage[];
      const room = Math.max(0, MAX_IMAGES - pendingRef.current.length);
      const accepted = imgs.slice(0, room);
      if (accepted.length) setPendingImages((prev) => [...prev, ...accepted]);
      if (imgs.length > accepted.length) setAttachNote(`Max ${MAX_IMAGES} images per message — ${imgs.length - accepted.length} not added.`);
    }

    for (const file of docs) {
      if (file.size > 50 * 1024 * 1024) { setAttachNote(`${file.name} is over 50 MB — skipped.`); continue; }
      try {
        const data = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve((r.result as string).split(',')[1] || '');
          r.onerror = () => reject(r.error);
          r.readAsDataURL(file);
        });
        const res = await fetch(`/api/gw/sessions/${slug}/upload`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name, data }),
        });
        const d = await res.json();
        if (d.ok) setPendingDocs((prev) => [...prev, { name: file.name, path: d.path }]);
        else setAttachNote(`Upload failed (${file.name}): ${d.error || 'error'}`);
      } catch (e) {
        setAttachNote(`Upload failed (${file.name})`);
      }
    }
  }, [slug]);


  return (
    <div className="flex flex-col h-full min-w-0"
      onDragOver={(e) => { if ([...e.dataTransfer.types].includes('Files')) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { if (![...e.dataTransfer.types].includes('Files')) return; e.preventDefault(); setDragOver(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); }}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 shrink-0">
        <span className={`w-2 h-2 rounded-full ${streaming ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500'}`} />
        <span className="font-semibold text-sm text-gray-100">{name}</span>
        {onRename && (
          <button onClick={onRename} title="Rename project / session"
            className="text-gray-600 hover:text-gray-200 text-[11px] leading-none">✎</button>
        )}
        <span className="text-[11px] text-gray-500 font-mono">{slug}</span>
        <div className="ml-auto flex items-center gap-2">
          {isClaire && (
            <div className="relative">
              <button onClick={() => setGamesOpen((v) => !v)}
                className="text-sm px-3 py-1.5 rounded-full font-bold text-white shadow-lg flex items-center gap-1"
                style={{ background: 'linear-gradient(135deg,#ff7ac0,#7be8b5)' }}
                title="Open Claire's games">🎮 Games <span className="text-[10px] opacity-80">{gamesOpen ? '▲' : '▼'}</span></button>
              {gamesOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setGamesOpen(false)} />
                  <div className="absolute right-0 mt-2 z-50 w-56 rounded-2xl p-2 shadow-2xl border border-white/10"
                    style={{ background: '#1b1030' }}>
                    <div className="text-[11px] font-bold text-pink-200 px-2 pb-1.5">🎀 Claire's Games</div>
                    <div className="flex flex-col gap-1.5">
                      {CLAIRE_GAMES.map((g) => (
                        <button key={g.url}
                          onClick={() => { window.dispatchEvent(new CustomEvent('beast:open-game', { detail: { url: g.url, name: g.name, anchorSlug: 'claire' } })); setGamesOpen(false); }}
                          className="w-full text-left text-sm px-3 py-2 rounded-xl font-bold text-white shadow hover:brightness-110 transition flex items-center gap-2"
                          style={{ background: g.bg }}>
                          <span className="text-lg">{g.emoji}</span> {g.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          <button onClick={() => { setSearchOpen((v) => !v); requestAnimationFrame(() => searchInputRef.current?.focus()); }}
            className={`text-[11px] px-2 py-1 rounded hover:bg-white/10 ${searchOpen ? 'text-yellow-300 bg-white/10' : 'text-gray-500 hover:text-gray-200'}`}
            title="Search this chat (⌘F)">🔍</button>
          {streaming && (
            <button onClick={abort} className="text-[11px] px-2 py-1 rounded bg-red-500/15 text-red-300 hover:bg-red-500/25">
              Stop
            </button>
          )}
          <button onClick={() => { if (confirm(`Clear the ${name} chat and start a fresh session context? History is erased.`)) clear(); }}
            className="text-[11px] px-2 py-1 rounded text-gray-500 hover:text-gray-200 hover:bg-white/10" title="Clear chat + fresh session context">
            Clear
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 bg-white/[0.03] shrink-0">
          <span className="text-gray-500 text-xs">🔍</span>
          <input ref={searchInputRef} value={query} onChange={(e) => setQuery(e.target.value)} autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); jumpTo(matchIdx + (e.shiftKey ? -1 : 1)); }
              else if (e.key === 'Escape') { setSearchOpen(false); setQuery(''); }
            }}
            placeholder="Search this chat…  (Enter ↓ · Shift+Enter ↑)"
            className="flex-1 bg-transparent text-sm text-gray-100 outline-none placeholder-gray-600" />
          <span className="text-[11px] text-gray-500 tabular-nums min-w-[44px] text-right">
            {query.trim() ? `${matchIds.length ? matchIdx + 1 : 0}/${matchIds.length}` : ''}
          </span>
          <button onClick={() => jumpTo(matchIdx - 1)} disabled={!matchIds.length} title="Previous (Shift+Enter)"
            className="text-gray-400 hover:text-gray-100 disabled:opacity-25 px-1 leading-none">↑</button>
          <button onClick={() => jumpTo(matchIdx + 1)} disabled={!matchIds.length} title="Next (Enter)"
            className="text-gray-400 hover:text-gray-100 disabled:opacity-25 px-1 leading-none">↓</button>
          <button onClick={() => { setSearchOpen(false); setQuery(''); }} title="Close (Esc)"
            className="text-gray-500 hover:text-gray-200 px-1 leading-none">✕</button>
        </div>
      )}

      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {!historyHydrated && <div className="text-center text-gray-500 text-sm py-8">Loading history…</div>}
        {historyHydrated && messages.length === 0 && !streaming && (
          <div className="text-center text-gray-500 text-sm py-8">
            Fresh session for <b className="text-gray-300">{name}</b> — full history will appear here.
          </div>
        )}
        {messages.length > windowSize && (
          <button onClick={() => setWindowSize((w) => w + 200)}
            className="mx-auto block text-[11px] text-gray-500 hover:text-gray-300 px-3 py-1.5 rounded bg-white/5">
            Show earlier messages ({messages.length - windowSize} hidden)
          </button>
        )}
        {visibleMessages.map((m) => <MessageRow key={m.id} m={m} match={matchSet.has(m.id)} current={currentId === m.id} onDelete={handleDeleteMessage} />)}
        {streaming && (
          <div className="flex justify-start">
            <div className="max-w-[92%] text-sm text-gray-200 min-w-0">
              {streamParts.map((p, i) =>
                p.type === 'text'
                  ? <MarkdownBlock key={i} text={p.content} />
                  : p.type === 'thinking'
                  ? <div key={i} className="text-xs text-gray-500 italic whitespace-pre-wrap border-l-2 border-gray-700 pl-2 my-1">{p.content}</div>
                  : <ToolBlock key={p.tool.id || i} name={p.tool.name} input={p.tool.input} output={p.tool.output} status={p.tool.status} />,
              )}
              {streamText && <MarkdownBlock text={streamText} />}
              {thinking && !streamText && (
                <div className="flex items-center gap-2 text-gray-500 text-xs">
                  <span className="w-3 h-3 border border-gray-500 border-t-gray-300 rounded-full animate-spin" />
                  thinking…
                </div>
              )}
            </div>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-3 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            <span className="flex-1">{error}</span>
            {canRetry && (
              <button onClick={retry} className="px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/30 font-semibold shrink-0">Retry</button>
            )}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {pendingDelete && (
        <div className="flex justify-center shrink-0" style={{ height: 0, overflow: 'visible' }}>
          <div style={{ position: 'relative', top: -46, display: 'flex', alignItems: 'center', gap: 10,
            padding: '7px 10px 7px 14px', borderRadius: 9, background: 'rgba(28,32,42,0.96)',
            border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
            fontSize: 12, color: '#c4ccd8', whiteSpace: 'nowrap', zIndex: 20 }}>
            <span>Message deleted &amp; forgotten</span>
            <button onClick={handleUndoDelete} className="hover:brightness-125"
              style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: '#67b8ff', fontWeight: 600, fontSize: 12, padding: '2px 4px' }}>
              Undo
            </button>
          </div>
        </div>
      )}

      <div className="px-4 pb-3 pt-1 shrink-0">
        {queued > 0 && (
          <div className="px-1 pb-1 text-[10px] text-amber-300/80 font-mono">
            {queued} message{queued > 1 ? 's' : ''} queued — sends when the current run finishes
          </div>
        )}
        {attachNote && (
          <div className="px-1 pb-1 text-[10px] text-amber-300/80 font-mono">{attachNote}</div>
        )}
        {pendingImages.length > 0 && (
          <div className="flex gap-2 pb-2 flex-wrap">
            {pendingImages.map((img, i) => (
              <span key={i} className="relative">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={`data:${img.media_type};base64,${img.data}`} alt={`attachment ${i + 1}`}
                  className="w-14 h-14 object-cover rounded-lg border border-white/15" />
                <button onClick={() => setPendingImages((prev) => prev.filter((_, x) => x !== i))}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-gray-900 border border-white/20 text-gray-300 text-[9px] leading-none hover:text-white">✕</button>
              </span>
            ))}
          </div>
        )}
        {pendingDocs.length > 0 && (
          <div className="flex gap-2 pb-2 flex-wrap">
            {pendingDocs.map((d, i) => (
              <span key={i} className="flex items-center gap-1.5 bg-white/5 border border-white/15 rounded-lg px-2 py-1 text-[11px] text-gray-200"
                title={`saved to ${d.path}`}>
                📄 <span className="max-w-[140px] truncate">{d.name}</span>
                <span className="text-[9px] text-gray-500 font-mono">{d.path.replace(/\/[^/]*$/, '/')}</span>
                <button onClick={() => setPendingDocs((prev) => prev.filter((_, x) => x !== i))}
                  className="text-gray-500 hover:text-white leading-none">✕</button>
              </span>
            ))}
          </div>
        )}
        <ChatInput name={name} streaming={streaming} dragOver={dragOver}
          hasImages={pendingImages.length > 0 || pendingDocs.length > 0}
          onSend={handleSend} onPickFiles={() => fileRef.current?.click()} onPasteFiles={addFiles} />
        {/* images go inline as vision; any other file uploads to the project */}
        <input ref={fileRef} type="file" multiple className="hidden"
          onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }} />
        <div className="pt-1.5">
          <SessionStatusBar slug={slug} main={main} streaming={streaming} />
        </div>
      </div>

    </div>
  );
}


/** Isolated input — local keystroke state so typing NEVER re-renders the
 *  (potentially huge) transcript above. */
function ChatInput({ name, streaming, dragOver, hasImages, onSend, onPickFiles, onPasteFiles }: {
  name: string; streaming: boolean; dragOver: boolean; hasImages: boolean;
  onSend: (text: string) => void; onPickFiles: () => void; onPasteFiles: (files: File[]) => void;
}) {
  const [input, setInput] = useState('');
  // Prefill the composer from handoffs (project "Start task" / roadmap via
  // beast-pending-task, brain MAP buttons via ?msg=). Prefill only — never
  // auto-send; the user reviews and hits Send.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const msg = params.get('msg');
      if (msg) {
        setInput(msg);
        params.delete('msg');
        const qs = params.toString();
        window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
        return;
      }
      const raw = localStorage.getItem('beast-pending-task');
      if (raw) {
        localStorage.removeItem('beast-pending-task');
        const pending = JSON.parse(raw);
        if (pending?.prompt) setInput(pending.prompt);
      }
    } catch { try { localStorage.removeItem('beast-pending-task'); } catch {} }
  }, []);
  const submit = () => {
    const text = input.trim();
    if (!text && !hasImages) return;
    setInput('');
    onSend(text);
  };
  return (
    <div className={`flex items-end gap-2 rounded-xl border px-3 py-2 focus-within:border-white/20 ${dragOver ? 'border-cyan-400/60 bg-cyan-500/5' : 'border-white/10 bg-white/5'}`}>
      <button onClick={onPickFiles} title="Attach images (or paste / drag-drop)"
        className="text-gray-500 hover:text-gray-200 text-base leading-none pb-0.5">📎</button>
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
        onPaste={(e) => {
          const files = [...e.clipboardData.items].filter((i) => i.kind === 'file').map((i) => i.getAsFile()).filter(Boolean) as File[];
          if (files.length) { e.preventDefault(); onPasteFiles(files); }
        }}
        placeholder={streaming ? `Working… your message steers the current run` : `Message ${name}…`}
        rows={Math.min(6, Math.max(1, input.split('\n').length))}
        className="flex-1 bg-transparent outline-none resize-none text-sm text-gray-100 placeholder-gray-500"
      />
      <button onClick={submit} disabled={!input.trim() && !hasImages}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-30 hover:bg-blue-500">
        {streaming ? 'Steer' : 'Send'}
      </button>
    </div>
  );
}
